import React, { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../../components/TanTable';
import { useViewedPros } from '../../../contexts/ViewedProsContext';
import Toggle from '../../../components/Toggle';
import type { QuoteListRow } from '../fixtures';
import { laneAllInTotal } from '../laneSummary';
import { statusBadgeClass } from '../quoteColors';

interface Props {
  lanes: QuoteListRow[];
  /** Row click — open the editor drawer for this lane. */
  onOpenLane: (lane: QuoteListRow) => void;
  /** "+ Add Quote" in the table header. */
  onAddQuote: () => void;
  /** "Ended" header toggle — when on, the latest revision of already-ended
   *  (expired) quotes is shown alongside the active ones. Off by default. */
  showEnded: boolean;
  onToggleEnded: (next: boolean) => void;
  /** True while the ended quotes are being fetched (disables the toggle). */
  endedLoading?: boolean;
}

/** "40 HC" equipment summary from size + equipType. */
function equipSummary(lane: QuoteListRow): string {
  return [lane.size, lane.equipType].filter(Boolean).join(' ');
}
/** "40 HC · IMPORT" — equipment summary plus move type. */
function equipMove(lane: QuoteListRow): string {
  return [equipSummary(lane), lane.moveType].filter(Boolean).join(' · ');
}
/** Base dray as "$850.00"; "—" when absent. */
function formatDray(raw: string | undefined): string {
  return raw ? `$${raw}` : '—';
}
/** All-in total as "$1,037.00"; "—" when not computable. */
function formatTotal(n: number | null): string {
  if (n == null) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
/** "18.25%" actual margin; "—" when absent. */
function formatMargin(v: number | null | undefined): string {
  return v == null ? '—' : `${v.toFixed(2)}%`;
}

/**
 * The (customer, bill-to) group's Direct Quotes, rendered with the shared
 * TanTable: every data column sorts/filters, the toolbar exports to Excel, and
 * the layout persists per user (settingsKey). Clicking anywhere in the row
 * opens the editor drawer. Replaces the bespoke LaneTable. Customer / Bill-To
 * columns are omitted — the group already IS the customer + bill-to. Adds a
 * Margin % column.
 */
const DirectQuotesTable: React.FC<Props> = ({ lanes, onOpenLane, onAddQuote, showEnded, onToggleEnded, endedLoading = false }) => {
  // Marks a quote viewed when its row is opened; the Quote # column shows the
  // green pill + last-opened ring. No-op without a ViewedProsProvider.
  const { markViewed } = useViewedPros();
  const columns = useMemo<ColumnDef<QuoteListRow>[]>(() => [
    {
      id: 'quoteId',
      accessorFn: (l) => l.quoteId,
      header: 'Quote #',
      // Plain text — the whole row opens the editor drawer (onRowClick). The
      // EXPIRED status badge already flags ended quotes, so the number stays
      // plain for every row.
      cell: ({ row }) => (
        <span className="font-semibold text-black dark:text-slate-100">{row.original.quoteId}</span>
      ),
    },
    {
      id: 'lane',
      accessorFn: (l) => l.lane,
      header: 'Lane',
      cell: ({ row }) => (
        <span className="font-medium text-black dark:text-slate-100">{row.original.lane || '—'}</span>
      ),
    },
    {
      id: 'equipMove',
      accessorFn: (l) => equipMove(l),
      header: 'Equip · Move',
      cell: ({ row }) => <span>{equipMove(row.original) || '—'}</span>,
    },
    {
      id: 'dray',
      accessorFn: (l) => l.drayRate ?? '',
      header: 'Dray',
      cell: ({ row }) => <span>{formatDray(row.original.drayRate)}</span>,
    },
    {
      id: 'total',
      accessorFn: (l) => laneAllInTotal(l) ?? undefined,
      sortUndefined: 'last',
      header: 'Total',
      cell: ({ row }) => (
        <span className="font-semibold text-black dark:text-slate-100">{formatTotal(laneAllInTotal(row.original))}</span>
      ),
    },
    {
      id: 'margin',
      accessorFn: (l) => l.actualMargin ?? undefined,
      sortUndefined: 'last',
      header: 'Margin %',
      cell: ({ row }) => <span>{formatMargin(row.original.actualMargin)}</span>,
    },
    {
      id: 'status',
      accessorFn: (l) => l.status,
      header: 'Status',
      cell: ({ row }) => (
        <span className={['inline-block px-2 py-[2px] text-[0.625rem] font-bold uppercase tracking-wider rounded border', statusBadgeClass(row.original.status)].join(' ')}>
          {row.original.status}
        </span>
      ),
    },
    {
      id: 'effective',
      accessorFn: (l) => l.effective || '',
      header: 'Effective',
      cell: ({ row }) => <span>{row.original.effective || '—'}</span>,
    },
    {
      id: 'endDate',
      accessorFn: (l) => l.expires || '',
      header: 'End Date',
      cell: ({ row }) => <span>{row.original.expires || '—'}</span>,
    },
    {
      id: 'lastUsed',
      accessorFn: (l) => l.lastUsedDate || '',
      header: 'Last Used',
      cell: ({ row }) => <span>{row.original.lastUsedDate || '—'}</span>,
    },
    {
      id: 'days',
      accessorFn: (l) => l.daysOut ?? '',
      header: 'Days',
      cell: ({ row }) => <span>{row.original.daysOut != null ? row.original.daysOut : '—'}</span>,
    },
    {
      id: 'soldBy',
      accessorFn: (l) => l.soldBy || '',
      header: 'Sold By',
      cell: ({ row }) => <span>{row.original.soldBy || '—'}</span>,
    },
    {
      id: 'createDate',
      accessorFn: (l) => l.createdDate || '',
      header: 'Create Date',
      cell: ({ row }) => <span>{row.original.createdDate || '—'}</span>,
    },
  ], []);

  return (
    <TanTable<QuoteListRow>
      data={lanes}
      columns={columns}
      trackViewedColumnId="quoteId"
      getRowId={(l) => l.quoteId}
      settingsKey="quotes-group-direct"
      cardTitle={`Direct Quotes (${lanes.length})`}
      hideTitleCount
      onRowClick={(l) => { markViewed(l.quoteId); onOpenLane(l); }}
      searchPrefix={(
        // Off by default; when on, the table also lists the latest revision of
        // quotes that have already ended (expired). Sits to the LEFT of search.
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <Toggle
            checked={showEnded}
            onChange={onToggleEnded}
            size="sm"
            disabled={endedLoading}
            ariaLabel="Show ended quotes"
          />
          <span className="text-[0.71875rem] font-medium text-white">Ended</span>
        </label>
      )}
      headerExtra={(
        <button
          type="button"
          onClick={onAddQuote}
          className="px-2.5 py-1 text-[0.71875rem] font-semibold text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-50 dark:bg-slate-800 dark:text-blue-300 dark:border-blue-700"
        >
          + Add Quote
        </button>
      )}
    />
  );
};

export default DirectQuotesTable;
